import { useEffect, useMemo, useRef, useState } from "react";
import { Outlet, useNavigate, useParams } from "react-router-dom";
import ProfileCard from "../../../../components/ProfileCard";
import styles from "./entryPoint.module.scss";
import { LeadsProps } from "../../../../interfaces/pages";
import { CircularProgress } from "@mui/material";
import LeadCard from "./LeadCard";
import { FAKE_LEAD } from "../../../../constants/leads";
import ButtonShared from "../../../../components/Button/Button";
import moment from "moment";
let interval: any;

const EntryPoint = ({
    leads,
    getLeads,
    getLeadsByBrand,
    fetchingLeads,
    fetchingPublication,
    publication,
    getPublication,
    user
}: LeadsProps) => {
    const navigate = useNavigate();
    const params = useParams();
    const [filter, setFilter] = useState("1")
    const startDate = useRef("");
    const endDate = useRef("");
    const stringQuery = useRef({
        opportunityPage: 1,
        registerPage: 1,
        offerPage: 1,
        schedulingPage: 1,
        matchPage: 1,
        referredPage: 1,
        soldPage: 1,
        not_interestedPage: 1,
        informationPage: 1,
        reservePage: 1
    })

    const isAdminBrand = user?.profile?.code === "admin-brand";

    const LEAD_COLUMNS = useMemo(() => {
        if (isAdminBrand) {
            // Columnas específicas para admin-brand
            return [
                {
                    id: "opportunity",
                    label: "Oportunidad",
                    data: leads?.["opportunity"],
                    color: "82, 0, 255",
                    stringQuery: "opportunityPage"
                },
                {
                    id: "information",
                    label: "Cotización",
                    data: leads?.["information"],
                    color: "255, 94, 0",
                    stringQuery: "informationPage"
                },
                {
                    id: "reserve",
                    label: "Reserva",
                    data: leads?.["reserve"],
                    color: "13, 154, 255",
                    stringQuery: "reservePage"
                },
                {
                    id: "sold",
                    label: "Entrega",
                    data: leads?.["sold"],
                    color: "255, 0, 123",
                    stringQuery: "soldPage"
                }
            ];
        } else {
            // Columnas originales para otros usuarios
            return [
                {
                    id: "opportunity",
                    label: "Oportunidad",
                    data: leads?.["opportunity"],
                    color: "82, 0, 255",
                    stringQuery: "opportunityPage"
                },
                {
                    id: "offer",
                    label: "Oferta",
                    data: leads?.["offer"],
                    color: "255, 94, 0",
                    stringQuery: "offerPage"
                },
                {
                    id: "scheduling",
                    label: "Agendamiento",
                    data: leads?.["scheduling"],
                    color: "13, 154, 255",
                    stringQuery: "schedulingPage"
                },
                {
                    id: "match",
                    label: "Match",
                    data: leads?.["match"],
                    color: "241, 205, 0",
                    stringQuery: "matchPage"
                },
                {
                    id: "referred",
                    label: "Derivado",
                    data: leads?.["referred"],
                    color: "0, 0, 0",
                    stringQuery: "referredPage"
                },
                {
                    id: "sold",
                    label: "Entrega",
                    data: leads?.["sold"],
                    color: "255, 0, 123",
                    stringQuery: "soldPage"
                }
            ];
        }
    }, [leads, user]);

    // Función helper para usar la acción correcta según el tipo de usuario
    const getLeadsAction = (withLoading?: string, startDate?: string, endDate?: string, stringQuery?: any) => {
        if (isAdminBrand) {
            return getLeadsByBrand(withLoading, { startDate, endDate, ...stringQuery });
        } else {
            return getLeads(withLoading, startDate, endDate, stringQuery);
        }
    };

    type StringQueryKeys = keyof typeof stringQuery.current;

    const pressShowMore = (stringQueryText: StringQueryKeys) => {
        stringQuery.current[stringQueryText] = stringQuery.current[stringQueryText] + 1
        getLeadsAction("reloading", startDate.current, endDate.current, stringQuery.current);
    }

    const pressFilter = (filterText: string) => {
        stringQuery.current = {
            opportunityPage: 1,
            registerPage: 1,
            offerPage: 1,
            schedulingPage: 1,
            matchPage: 1,
            referredPage: 1,
            soldPage: 1,
            not_interestedPage: 1,
            informationPage: 1,
            reservePage: 1
        }
        if (filterText === 'all') {
            getLeadsAction('filter', "", "");
            startDate.current = ""
            endDate.current = ""
            setFilter("all");
        }
        if (filterText === '90') {
            getLeadsAction('filter', moment().subtract(90, 'd').format('YYYY-MM-DD'), moment().format('YYYY-MM-DD'));
            startDate.current = moment().subtract(90, 'd').format('YYYY-MM-DD')
            endDate.current = moment().format('YYYY-MM-DD');
            setFilter("90");
        }
        if (filterText === '30') {
            getLeadsAction('filter', moment().subtract(30, 'd').format('YYYY-MM-DD'), moment().format('YYYY-MM-DD'));
            startDate.current = moment().subtract(30, 'd').format('YYYY-MM-DD')
            endDate.current = moment().format('YYYY-MM-DD');
            setFilter("30");
        }
        if (filterText === '15') {
            getLeadsAction('filter', moment().subtract(15, 'd').format('YYYY-MM-DD'), moment().format('YYYY-MM-DD'));
            startDate.current = moment().subtract(15, 'd').format('YYYY-MM-DD')
            endDate.current = moment().format('YYYY-MM-DD');
            setFilter("15");
        }
        if (filterText === '7') {
            getLeadsAction('filter', moment().subtract(7, 'd').format('YYYY-MM-DD'), moment().format('YYYY-MM-DD'));
            startDate.current = moment().subtract(7, 'd').format('YYYY-MM-DD')
            endDate.current = moment().format('YYYY-MM-DD');
            setFilter("7");
        }
        if (filterText === '3') {
            getLeadsAction('filter', moment().subtract(3, 'd').format('YYYY-MM-DD'), moment().format('YYYY-MM-DD'));
            startDate.current = moment().subtract(3, 'd').format('YYYY-MM-DD')
            endDate.current = moment().format('YYYY-MM-DD');
            setFilter("3");
        }
        if (filterText === '1') {
            getLeadsAction('filter', moment().format('YYYY-MM-DD'), moment().format('YYYY-MM-DD'));
            startDate.current = moment().format('YYYY-MM-DD')
            endDate.current = moment().format('YYYY-MM-DD');
            setFilter("1");
        }

    }

    useEffect(() => {
        // Establecer filtro de hoy como predeterminado al cargar
        pressFilter("1");
        interval = setInterval(() => {
            getLeadsAction("reloading", startDate.current, endDate.current);
        }, 60000);
        return () => clearInterval(interval);
    }, [getLeads, getLeadsByBrand, isAdminBrand]);

    useEffect(() => {
        if (fetchingLeads === "failure") {
            clearInterval(interval);
        }
    }, [fetchingLeads])

    return (
        params.id ? <Outlet /> :
            <>
                {fetchingLeads === "loading"
                    ?
                    <div className={styles.loaderLeads} id="loaderLeads">
                        <CircularProgress
                            size={styles.loader}
                            classes={{
                                colorPrimary: styles.loader
                            }}
                        />
                        <p className={styles.text}>Cargando Leads</p>
                    </div>
                    :
                    <ProfileCard classNameContainer={styles.offerCard} className={styles.leadCardContainer}>
                        <p className={styles.title}>
                            Leads
                            {fetchingLeads === "reloading" && <CircularProgress
                                size={"16px"}
                                className={styles.leadsLoader}
                                classes={{
                                    colorPrimary: styles.loader
                                }}
                            />}
                        </p>
                        <div className={styles.filter}>
                             <ButtonShared tertiary={filter !== 'all'} className={`${styles.button} ${filter === 'all' && styles.border}`}  title={"Todos"} onPress={() => pressFilter("all")} />
                             <ButtonShared tertiary={filter !== '90'} className={`${styles.button} ${filter === '90' && styles.border}`}  title={"3 meses"} onPress={() => pressFilter("90")} />
                             <ButtonShared tertiary={filter !== '30'} className={`${styles.button} ${filter === '30' && styles.border}`}  title={"1 mes"} onPress={() => pressFilter("30")} />
                             <ButtonShared tertiary={filter !== '15'} className={`${styles.button} ${filter === '15' && styles.border}`}  title={"15 dias"} onPress={() => pressFilter("15")} />
                             <ButtonShared tertiary={filter !== '7'} className={`${styles.button} ${filter === '7' && styles.border}`}  title={"7 dias"} onPress={() => pressFilter("7")} />
                             <ButtonShared tertiary={filter !== '3'} className={`${styles.button} ${filter === '3' && styles.border}`}  title={"3 dias"} onPress={() => pressFilter("3")} />
                             <ButtonShared tertiary={filter !== '1'} className={`${styles.button} ${filter === '1' && styles.border}`}  title={"Hoy"} onPress={() => pressFilter("1")} />
                        </div>
                        <div className={styles.leadsContainer}>
                            <div className={styles.list}>
                                {
                                    LEAD_COLUMNS.map((leadItem, index) => (
                                        <div className={`${styles.leadColumnContainer} ${LEAD_COLUMNS.length === index + 1 && styles.removeMargin}`}>
                                            <p style={{ color: `black` }} className={styles.leadTitle}>
                                                {leadItem.label}
                                                <div style={{ backgroundColor: "black" }} className={styles.ball} >
                                                    <span className={styles.columnCount}>
                                                        {leadItem?.data?.total}
                                                    </span>
                                                </div>
                                            </p>
                                            <div className={styles.itemsContainer}>
                                                {
                                                    (!leadItem?.data || !leadItem?.data?.leads?.length) ? <LeadCard fake={true} onClick={() => { }} item={FAKE_LEAD} isAdminBrand={isAdminBrand} /> : leadItem.data?.leads?.map((item: any) => (
                                                        <LeadCard onClick={(link) => navigate(`/profile/my_publications/${link.split("/")[link.split("/").length - 1]}/management`)} item={item} isAdminBrand={isAdminBrand} />
                                                    ))
                                                }
                                                {leadItem?.data?.leads?.length < leadItem?.data?.total
                                                    &&
                                                    //@ts-ignore
                                                    <ButtonShared secondary title={"Ver más"} onPress={() => pressShowMore(leadItem?.stringQuery)} />
                                                }
                                            </div>
                                        </div>
                                    ))
                                }
                            </div>
                        </div>
                    </ProfileCard>
                }
                {/*
                NO ELIMINAR TODO AGREGAR CHAT ETAPA 2
                <ChatModal 
                    open={openChat.open} 
                    onClose={() => setOpenChat(prev => ({...prev,open: false}))} 
                    item={openChat.lead} 
                    currentStatus={openChat.currentStatus}
                    statusColor={openChat.statusColor}
                    getPublication={getPublication}
                    fetchingPublication={fetchingPublication}
                    publication={publication}
                />*/}
            </>
    )
};
export default EntryPoint;